import { useEffect, useState } from "react";
import { PenSquare } from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useIsAdmin, useCurrentUser } from "@/hooks/useCurrentUser";
import { trackPageView, trackUserAction } from "@/lib/analytics";
import { queryClient } from "@/lib/queryClient";
import { mableHomeKeys, useMableHomeContent, useUpdateMableHomeContent } from "@/services/mableHome";
import { RichTextEditor } from "./components/RichTextEditor";

export function MableHomePage() {
  const isAdmin = useIsAdmin();
  const currentUser = useCurrentUser();
  const { data, isPending } = useMableHomeContent();
  const saveMutation = useUpdateMableHomeContent();

  const [isEditing, setIsEditing] = useState(false);
  const [draftHtml, setDraftHtml] = useState("");

  useEffect(() => {
    trackPageView("mable_home_loaded", currentUser?.email ?? "anonymous");
  }, [currentUser?.email]);

  useEffect(() => {
    if (data?.htmlContent && !isEditing) {
      setDraftHtml(data.htmlContent);
    }
  }, [data?.htmlContent, isEditing]);

  async function handleSave() {
    const nextHtml = draftHtml.trim();
    if (!nextHtml) {
      toast.error("Content cannot be empty");
      return;
    }

    try {
      const saved = await saveMutation.mutateAsync({ htmlContent: nextHtml });
      queryClient.setQueryData(mableHomeKeys.content(), saved);
      setDraftHtml(saved.htmlContent);
      setIsEditing(false);

      trackUserAction("Mable Home", currentUser?.email ?? "anonymous", "mable_home_content_saved");
      toast.success("Mable Home content saved");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  function getErrorMessage(error: unknown): string {
    if (!(error instanceof Error)) {
      return "Unable to update Mable home content.";
    }

    const responseBody = error.message.slice(error.message.indexOf(": {") + 2);
    try {
      const response = JSON.parse(responseBody) as { errors?: string[]; message?: string };
      return response.errors?.[0] ?? response.message ?? "Unable to update Mable home content.";
    } catch {
      return "Unable to update Mable home content.";
    }
  }

  function handleCancel() {
    setIsEditing(false);
    setDraftHtml(data?.htmlContent ?? draftHtml);
  }

  function handleStartEdit() {
    setIsEditing(true);
    trackUserAction("Mable Home", currentUser?.email ?? "anonymous", "mable_home_edit_icon_clicked");
  }

  return (
    <section className="mable-home-page" aria-labelledby="mable-home-title">
      <header className="mable-home-header">
        {/* <h1 id="mable-home-title" className="mable-home-title">
          <Link
            to="/home"
            onClick={() => trackUserAction("Mable Home", currentUser?.email ?? "anonymous", "mable_title_link_clicked")}
          >
            Mable
          </Link>
        </h1> */}

        {isAdmin ? (
          <div className="mable-home-actions">
            {!isEditing ? (
              <button type="button" className="mable-home-edit-button" onClick={handleStartEdit} aria-label="Edit Mable Home content">
                <PenSquare className="h-4 w-4" />
                Edit
              </button>
            ) : (
              <>
                <button type="button" className="mable-home-save-button" onClick={() => void handleSave()} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </button>
                <button type="button" className="mable-home-cancel-button" onClick={handleCancel} disabled={saveMutation.isPending}>
                  Cancel
                </button>
              </>
            )}
          </div>
        ) : null}
      </header>

      <article className="mable-home-content" aria-live="polite">
        {isPending ? <p>Loading resource content...</p> : null}

        {!isPending && !isEditing ? (
          <div className="mable-home-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data?.htmlContent ?? "") }} />
        ) : null}

        {isEditing ? (
          <RichTextEditor
            className="mable-home-editor"
            content={draftHtml}
            onChange={(html) => setDraftHtml(html)}
          />
        ) : null}
      </article>
    </section>
  );
}
